import { createSlice } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import { noop } from 'mojito/utils';
import serviceFactory from './service/service-factory.js';
import { actions as eventsActions } from 'services/sports-content/events/slice.js';

const reduxInstance = MojitoCore.Services.redux;

/**
 * The name of the favorites store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.Favorites
 */
export const STORE_KEY = 'favoritesStore';

export const INITIAL_STATE = {
    events: [],
    shouldReturnToFavoritesTab: false,
};

export const { reducer, actions } = createSlice({
    name: 'favorites',
    initialState: INITIAL_STATE,
    reducers: {
        addFavoriteEventIdSuccess(state, { payload: eventId }) {
            if (!state.events.includes(eventId)) {
                state.events.push(eventId);
            }
        },
        removeFavoriteEventIdSuccess(state, { payload: eventId }) {
            state.events = state.events.filter(id => id !== eventId);
        },
        fetchFavoriteEventIdsSuccess(state, { payload: events }) {
            state.events = events;
        },
        setShouldReturnToFavoritesTab(state, { payload: value }) {
            state.shouldReturnToFavoritesTab = value;
        },
        fetchFavoriteEventIdsFailed: noop,
        reset() {
            return INITIAL_STATE;
        },
    },
});

actions.init = config => () => {
    serviceFactory.init(config);
};

actions.fetchFavoriteEventIds = () => {
    return dispatch => {
        serviceFactory.getService().fetchFavoriteEventIds(
            data => dispatch(actions.fetchFavoriteEventIdsSuccess(data)),
            err => dispatch(actions.fetchFavoriteEventIdsFailed(err))
        );
    };
};

actions.addFavoriteEventId = eventId => {
    return dispatch => {
        serviceFactory
            .getService()
            .addFavoriteEventId(eventId)
            .then(() => dispatch(actions.addFavoriteEventIdSuccess(eventId)));
    };
};

actions.removeFavoriteEventId = eventId => {
    return dispatch => {
        serviceFactory
            .getService()
            .removeFavoriteEventId(eventId)
            .then(() => dispatch(actions.removeFavoriteEventIdSuccess(eventId)));
    };
};

reduxInstance.actionListener.startListening({
    actionCreator: actions.fetchFavoriteEventIdsSuccess,
    effect: (action, listenerApi) => {
        const eventIds = action.payload;
        if (!eventIds?.length) {
            return;
        }
        // We subscribe to all favourite events to make sure all of them are still valid and exist.
        // If an event is over, then it will be removed from favourites in eventsActions.updateEvent listener.
        // We assume that it is very unlikely that an event that was removed will ever get back in a real-life scenario.
        listenerApi.dispatch(
            eventsActions.subscribeEventsChunk({
                requests: [{ clientId: STORE_KEY, itemIds: eventIds }],
            })
        );
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: actions.addFavoriteEventIdSuccess,
    effect: (action, listenerApi) => {
        const eventId = action.payload;
        listenerApi.dispatch(
            eventsActions.subscribeEvents({ clientId: STORE_KEY, eventIds: [eventId] })
        );
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: actions.removeFavoriteEventIdSuccess,
    effect: (action, listenerApi) => {
        const eventId = action.payload;
        listenerApi.dispatch(eventsActions.unsubscribeEvents([eventId], STORE_KEY));
    },
});

reduxInstance.actionListener.startListening({
    actionCreator: eventsActions.updateEvent,
    effect: (action, listenerApi) => {
        const { eventId, event } = action.payload;
        if (!event) {
            const favoriteEventIds = listenerApi.getState()[STORE_KEY].events;
            const isFavoriteEvent = favoriteEventIds.includes(eventId);
            if (isFavoriteEvent) {
                listenerApi.dispatch(actions.removeFavoriteEventId(eventId));
            }
        }
    },
});

/**
 * Config object used to initialise favorites service layer.
 *
 * @typedef FavoritesConfig
 * @type {object}
 * @property {Mojito.Services.Favorites.AbstractFavoritesService} service - Favorites service implementation.
 *
 * @memberof Mojito.Services.Favorites
 */

/**
 * The state of favorites store slice.
 *
 * @typedef FavoritesStoreState
 * @type {object}
 * @property {Array<string>} events - Array of favorite events.
 * @property {boolean} shouldReturnToFavoritesTab - Should select the favourite tab if we navigate to a favourite event.
 *
 * @memberof Mojito.Services.Favorites
 */

reduxInstance.injectReducer(STORE_KEY, reducer);
